import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  Users,
  Clock,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  BarChart2,
  Activity,
  ChevronDown,
  ChevronUp,
  Search,
  Download,
  RefreshCw,
  User,
  MapPin,
  Award,
  Target,
  AlertCircle,
  Filter,
  ArrowUpDown,
  Database
} from 'lucide-react';
import AILoadingOverlay from '../common/AILoadingOverlay';
import './CollectorDashboard.css';
import { AnimatedCard, AnimatedNumber, useAnimateOnMount } from '../common/AnimationUtils';

// const API_BASE_URL = 'http://localhost:8000';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Responsive hook
const useWindowWidth = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
};

// Cache key prefix for localStorage
const CACHE_KEY_PREFIX = 'dataquad_collector_';

// Quality score color function (consistent with DataSummary)
const getQualityColor = (score, isDarkMode) => {
  if (score >= 90) return '#10B981'; // Green - Excellent
  if (score >= 70) return '#F59E0B'; // Yellow/Amber - Good
  if (score >= 50) return '#F97316'; // Orange - Fair
  return '#EF4444'; // Red - Poor
};

const getQualityGrade = (score) => {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

const getGradeColor = (grade, isDarkMode) => {
  const colors = {
    'A': '#10B981',
    'B': '#34D399',
    'C': '#F59E0B',
    'D': '#F97316',
    'F': '#EF4444'
  };
  return colors[grade] || '#6B7280';
};

const CollectorDashboard = ({
  primaryData,
  backcheckData,
  projectId,  // NEW: Project ID for server-side analysis (Phase 2)
  collectorIdColumn: externalCollectorIdColumn, // Rename prop to allow internal override
  interviewDateColumn: externalInterviewDateColumn, // Rename prop to allow internal override
  durationColumn: externalDurationColumn,
  startTimeColumn,
  endTimeColumn,
  latColumn,
  lonColumn,
  // NEW: XLSForm integration props
  xlsformData,
  xlsformMatchingResult
}) => {
  const { isDarkMode } = useTheme();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const [activeDataset, setActiveDataset] = useState('primary');
  const [activeTab, setActiveTab] = useState('collectors'); // 'collectors' | 'timeline'
  const [collectorResults, setCollectorResults] = useState(null);
  const [timelineResults, setTimelineResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'total_interviews', direction: 'desc' });
  const [filterFlags, setFilterFlags] = useState(false);
  
  // Internal column selections for COLLECTOR PERFORMANCE tab
  const [internalCollectorColumn, setInternalCollectorColumn] = useState('');
  const [collectorTabDateColumn, setCollectorTabDateColumn] = useState('');
  const [collectorTabDurationColumn, setCollectorTabDurationColumn] = useState('');
  
  // Internal column selections for TIMELINE PATTERNS tab (completely independent)
  const [timelineDateColumn, setTimelineDateColumn] = useState('');
  const [timelineCollectorColumn, setTimelineCollectorColumn] = useState('');
  const [timelineDurationColumn, setTimelineDurationColumn] = useState('');
  
  // Use internal selection if set, otherwise fall back to external props (for Collector Performance)
  const collectorIdColumn = internalCollectorColumn || externalCollectorIdColumn;
  const interviewDateColumn = collectorTabDateColumn || externalInterviewDateColumn;
  const durationColumn = collectorTabDurationColumn || externalDurationColumn;
  
  // Cache-related state (like DataSummary)
  const [forceRefresh, setForceRefresh] = useState(0);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState({ collectors: null, timeline: null });

  // ============================================================================
  // CACHE UTILITIES (same pattern as DataSummary)
  // ============================================================================
  
  const getCacheKey = useCallback((dataset, analysisType) => {
    // Create a unique key based on dataset type and analysis type
    const dataHash = primaryData?.total_rows || 0;
    return `${CACHE_KEY_PREFIX}${dataset}_${analysisType}_${dataHash}`;
  }, [primaryData?.total_rows]);

  const getFromCache = useCallback((dataset, analysisType) => {
    try {
      const key = getCacheKey(dataset, analysisType);
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (24 hours)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Error reading from cache:', e);
    }
    return null;
  }, [getCacheKey]);

  const saveToCache = useCallback((dataset, analysisType, data) => {
    try {
      const key = getCacheKey(dataset, analysisType);
      const cacheData = {
        ...data,
        timestamp: Date.now(),
        analyzedAt: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Error saving to cache:', e);
    }
  }, [getCacheKey]);

  const clearCache = useCallback((dataset, analysisType) => {
    try {
      const key = getCacheKey(dataset, analysisType);
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('Error clearing cache:', e);
    }
  }, [getCacheKey]);

  // Handle refresh button click
  const handleRefreshAnalysis = useCallback(() => {
    if (activeTab === 'collectors') {
      clearCache(activeDataset, 'collectors');
    } else {
      clearCache(activeDataset, 'timeline');
    }
    setForceRefresh(prev => prev + 1);
  }, [activeTab, activeDataset, clearCache]);

  // Get current data based on active dataset
  const currentData = useMemo(() => {
    return activeDataset === 'primary' ? primaryData : backcheckData;
  }, [activeDataset, primaryData, backcheckData]);

  // Get potential collector columns from data (any string or integer column could be a collector ID)
  const potentialCollectorColumns = useMemo(() => {
    if (!currentData?.column_types) return [];
    return Object.entries(currentData.column_types)
      .filter(([name, type]) => type === 'string' || type === 'integer')
      .map(([name]) => name);
  }, [currentData?.column_types]);

  // Get potential date columns from data - show ALL string columns since date types vary
  const potentialDateColumns = useMemo(() => {
    if (!currentData?.column_types) return [];
    // FInclude 'timestamp' which is what the backend actually uses for datetime columns
    return Object.entries(currentData.column_types)
      .filter(([name, type]) => 
        type === 'datetime' || 
        type === 'date' || 
        type === 'timestamp' ||  // <-- CRITICAL: Backend uses 'timestamp'
        type === 'string'
      )
      .map(([name]) => name);
  }, [currentData?.column_types]);

  // Get potential duration columns (numeric columns)
  const potentialDurationColumns = useMemo(() => {
    if (!currentData?.column_types) return [];
    // Show all numeric columns for duration
    return Object.entries(currentData.column_types)
      .filter(([name, type]) => type === 'integer' || type === 'float64')
      .map(([name]) => name);
  }, [currentData?.column_types]);

  // Auto-detect collector column on data load if not set
  useEffect(() => {
    if (!internalCollectorColumn && !externalCollectorIdColumn && potentialCollectorColumns.length > 0) {
      // Try to find a column that looks like a collector ID
      const likelyColumns = potentialCollectorColumns.filter(col => 
        col.toLowerCase().includes('collector') ||
        col.toLowerCase().includes('enumerator') ||
        col.toLowerCase().includes('interviewer') ||
        col.toLowerCase().includes('data_collector') ||
        col.toLowerCase().includes('surveyor') ||
        col.toLowerCase() === 'id_ter' ||
        col.toLowerCase() === 'idter'
      );
      if (likelyColumns.length > 0) {
        setInternalCollectorColumn(likelyColumns[0]);
      }
    }
  }, [potentialCollectorColumns, internalCollectorColumn, externalCollectorIdColumn]);

  // Auto-detect date column for Timeline tab on data load
  useEffect(() => {
    if (!timelineDateColumn && potentialDateColumns.length > 0) {
      // Try to find a column that looks like an interview date
      const likelyColumns = potentialDateColumns.filter(col => {
        const lowerCol = col.toLowerCase();
        return lowerCol.includes('interview') ||
               lowerCol.includes('submission') ||
               lowerCol.includes('submit') ||
               lowerCol.includes('starttime') ||
               lowerCol.includes('start_time') ||
               lowerCol === 'date' ||
               lowerCol === 'datetime';
      });
      if (likelyColumns.length > 0) {
        setTimelineDateColumn(likelyColumns[0]);
      }
    }
  }, [potentialDateColumns, timelineDateColumn]);

  // Auto-detect duration column for Timeline tab
  useEffect(() => {
    if (!timelineDurationColumn && potentialDurationColumns.length > 0) {
      const likelyColumns = potentialDurationColumns.filter(col => {
        const lowerCol = col.toLowerCase();
        return lowerCol.includes('duration') ||
               lowerCol.includes('minutes') ||
               lowerCol.includes('length');
      });
      if (likelyColumns.length > 0) {
        setTimelineDurationColumn(likelyColumns[0]);
      }
    }
  }, [potentialDurationColumns, timelineDurationColumn]);

  // Colors based on theme - Matching DataSummary exactly
  const colors = useMemo(() => ({
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    cardBorder: isDarkMode ? '#1F1F23' : '#E5E7EB',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#1A1A1D' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    textDim: isDarkMode ? '#6B7280' : '#9CA3AF',
    headerBg: isDarkMode ? '#0D0E0E' : '#F9FAFB',
    success: '#3aa092ff',
    warning: '#F59E0B',
    error: '#EF4444',
    orange: '#F97316',
    primary: '#2B8AFF',
    purple: '#8B5CF6',
    cyan: '#06B6D4',
    pink: '#EC4899',
    hover: isDarkMode ? '#1a1a1d' : '#F3F4F6',
    tableBorder: isDarkMode ? '#1F1F23' : '#E5E7EB',
    tableRowHover: isDarkMode ? '#1f1f24ff' : '#FAFAFA',
    rowAlt: isDarkMode ? '#29292cff' : '#FAFAFA',
  }), [isDarkMode]);

  // ============================================================================
  // FETCH COLLECTOR ANALYSIS (with caching)
  // ============================================================================
  
  const fetchCollectorAnalysis = useCallback(async (skipCache = false) => {
    const useServerSide = !!projectId;
    
    if (!useServerSide && !currentData?.full_data) return;
    if (!collectorIdColumn) return;

    // Check cache first (unless skipCache is true)
    if (!skipCache) {
      const cached = getFromCache(activeDataset, 'collectors');
      if (cached && cached.results) {
        setCollectorResults(cached.results);
        setLastAnalyzedAt(prev => ({ ...prev, collectors: cached.analyzedAt }));
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      // Prepare XLSForm data for the request (only for primary dataset)
      const xlsformPayload = (activeDataset === 'primary' && xlsformData?.parseResult) ? {
        xlsform_data: xlsformData.parseResult,
        variable_mapping: xlsformMatchingResult?.matches || {}
      } : {};
      
      let response;
      
      if (useServerSide) {
        // V2: Server loads data from DB
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (userInfo?.email) headers['user_email'] = userInfo.email;
        
        response = await fetch(`${API_BASE_URL}/analyze-collectors-v2`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            project_id: projectId,
            data_file_type: activeDataset === 'primary' ? 'primary' : 'backcheck',
            collector_id_column: collectorIdColumn,
            interview_date_column: interviewDateColumn || null,
            duration_column: durationColumn || null,
            start_time_column: startTimeColumn || null,
            end_time_column: endTimeColumn || null,
            lat_column: latColumn || null,
            lon_column: lonColumn || null,
            data_source: activeDataset === 'primary' ? 'Primary Survey' : 'Back-Check',
            ...xlsformPayload
          })
        });
      } else {
        // V1 fallback: Send data in request body
        response = await fetch(`${API_BASE_URL}/analyze-collectors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: currentData.full_data,
            collector_id_column: collectorIdColumn,
            interview_date_column: interviewDateColumn || null,
            duration_column: durationColumn || null,
            start_time_column: startTimeColumn || null,
            end_time_column: endTimeColumn || null,
            lat_column: latColumn || null,
            lon_column: lonColumn || null,
            data_source: activeDataset === 'primary' ? 'Primary Survey' : 'Back-Check',
            ...xlsformPayload
          })
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const data = await response.json();
      
      // Save to cache
      saveToCache(activeDataset, 'collectors', { results: data.results, xlsformIntegrated: data.xlsform_integrated });
      setCollectorResults(data.results);
      setLastAnalyzedAt(prev => ({ ...prev, collectors: new Date().toISOString() }));
    } catch (err) {
      setError(`Failed to analyze collectors: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentData, projectId, collectorIdColumn, interviewDateColumn, durationColumn, startTimeColumn, endTimeColumn, latColumn, lonColumn, activeDataset, getFromCache, saveToCache, xlsformData, xlsformMatchingResult]);

  // ============================================================================
  // FETCH TIMELINE ANALYSIS (with caching)
  // ============================================================================
  
  const fetchTimelineAnalysis = useCallback(async (skipCache = false) => {
    const useServerSide = !!projectId;
    
    if (!useServerSide && !currentData?.full_data) return;
    if (!timelineDateColumn) return;

    // Check cache first (unless skipCache is true)
    if (!skipCache) {
      const cached = getFromCache(activeDataset, 'timeline');
      if (cached && cached.results) {
        setTimelineResults(cached.results);
        setLastAnalyzedAt(prev => ({ ...prev, timeline: cached.analyzedAt }));
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      let response;
      
      if (useServerSide) {
        // V2: Server loads data from DB
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (userInfo?.email) headers['user_email'] = userInfo.email;
        
        response = await fetch(`${API_BASE_URL}/analyze-timeline-v2`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            project_id: projectId,
            data_file_type: activeDataset === 'primary' ? 'primary' : 'backcheck',
            interview_date_column: timelineDateColumn,
            start_time_column: startTimeColumn || null,
            end_time_column: endTimeColumn || null,
            duration_column: timelineDurationColumn || null,
            collector_id_column: timelineCollectorColumn || null,
            data_source: activeDataset === 'primary' ? 'Primary Survey' : 'Back-Check'
          })
        });
      } else {
        // V1 fallback: Send data in request body
        response = await fetch(`${API_BASE_URL}/analyze-timeline`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: currentData.full_data,
            interview_date_column: timelineDateColumn,
            start_time_column: startTimeColumn || null,
            end_time_column: endTimeColumn || null,
            duration_column: timelineDurationColumn || null,
            collector_id_column: timelineCollectorColumn || null,
            data_source: activeDataset === 'primary' ? 'Primary Survey' : 'Back-Check'
          })
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const data = await response.json();
      
      // Save to cache
      saveToCache(activeDataset, 'timeline', { results: data.results });
      setTimelineResults(data.results);
      setLastAnalyzedAt(prev => ({ ...prev, timeline: new Date().toISOString() }));
    } catch (err) {
      setError(`Failed to analyze timeline: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentData, projectId, timelineDateColumn, timelineDurationColumn, timelineCollectorColumn, startTimeColumn, endTimeColumn, activeDataset, getFromCache, saveToCache]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Run analysis when tab changes or forceRefresh is triggered
  useEffect(() => {
    if (activeTab === 'collectors' && collectorIdColumn) {
      fetchCollectorAnalysis(forceRefresh > 0);
    } else if (activeTab === 'timeline' && timelineDateColumn) {
      fetchTimelineAnalysis(forceRefresh > 0);
    }
    // Reset forceRefresh after use
    if (forceRefresh > 0) {
      setForceRefresh(0);
    }
  }, [activeTab, activeDataset, forceRefresh, collectorIdColumn, timelineDateColumn, fetchCollectorAnalysis, fetchTimelineAnalysis]);

  // Reset results when dataset changes
  useEffect(() => {
    // Try to load from cache for new dataset
    const cachedCollectors = getFromCache(activeDataset, 'collectors');
    const cachedTimeline = getFromCache(activeDataset, 'timeline');
    
    if (cachedCollectors?.results) {
      setCollectorResults(cachedCollectors.results);
      setLastAnalyzedAt(prev => ({ ...prev, collectors: cachedCollectors.analyzedAt }));
    } else {
      setCollectorResults(null);
    }
    
    if (cachedTimeline?.results) {
      setTimelineResults(cachedTimeline.results);
      setLastAnalyzedAt(prev => ({ ...prev, timeline: cachedTimeline.analyzedAt }));
    } else {
      setTimelineResults(null);
    }
  }, [activeDataset, getFromCache]);

  // Sort and filter collectors
  const sortedCollectors = useMemo(() => {
    if (!collectorResults?.collector_stats) return [];

    let filtered = [...collectorResults.collector_stats];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.collector_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Flag filter
    if (filterFlags) {
      filtered = filtered.filter(c => c.flags && c.flags.length > 0);
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortConfig.key] ?? 0;
      const bVal = b[sortConfig.key] ?? 0;
      const modifier = sortConfig.direction === 'asc' ? 1 : -1;
      return (aVal > bVal ? 1 : -1) * modifier;
    });

    return filtered;
  }, [collectorResults, searchTerm, filterFlags, sortConfig]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Get current lastAnalyzedAt based on active tab
  const currentLastAnalyzedAt = activeTab === 'collectors' 
    ? lastAnalyzedAt.collectors 
    : lastAnalyzedAt.timeline;

  // If no collector column AND no data loaded, show no data message
  if (!primaryData && !backcheckData) {
    return (
      <div className="nsx-metric-card" style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: colors.cardBg,
        borderRadius: '12px',
        margin: '20px',
        border: `1px solid ${colors.cardBorder}`
      }}>
        <Users size={48} style={{ color: colors.textMuted, marginBottom: '16px' }} />
        <h2 style={{ color: colors.text, marginBottom: '12px' }}>Collector Dashboard</h2>
        <p style={{ color: colors.textMuted, marginBottom: '24px' }}>
          Please load your data first to enable the collector dashboard.
        </p>
      </div>
    );
  }

  // If no collector column selected, show selector prompt
  if (!collectorIdColumn) {
    return (
      <div className="nsx-metric-card" style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: colors.cardBg,
        borderRadius: '12px',
        margin: '20px',
        border: `1px solid ${colors.cardBorder}`
      }}>
        <Users size={48} style={{ color: colors.textMuted, marginBottom: '16px' }} />
        <h2 style={{ color: colors.text, marginBottom: '12px' }}>Collector Dashboard</h2>
        <p style={{ color: colors.textMuted, marginBottom: '24px' }}>
          Select the data collector ID column to enable this dashboard.
        </p>
        
        {potentialCollectorColumns.length > 0 ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '16px' 
          }}>
            {/* Collector Column Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={16} style={{ color: colors.textMuted }} />
              <select
                value={internalCollectorColumn}
                onChange={(e) => setInternalCollectorColumn(e.target.value)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.cardBorder}`,
                  backgroundColor: colors.cardBg,
                  color: colors.text,
                  fontSize: '14px',
                  cursor: 'pointer',
                  outline: 'none',
                  minWidth: '220px'
                }}
              >
                <option value="">Select collector ID column...</option>
                {potentialCollectorColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            <p style={{ fontSize: '13px', color: colors.textDim, maxWidth: '400px' }}>
              Choose the column that identifies individual data collectors or enumerators.
            </p>
          </div>
        ) : (
          <p style={{ color: colors.textMuted }}>
            No suitable columns found. Please ensure your data has a collector ID column.
          </p>
        )}
      </div>
    );
  }

  // Dataset Toggle - Matching DataSummary EXACTLY
  const DatasetToggle = () => (
    <div className="cd-dataset-tabs">
      <button
        onClick={() => setActiveDataset('primary')}
        disabled={!primaryData}
        className={`cd-dataset-tab ${activeDataset === 'primary' ? 'active' : ''}`}
        style={{ color: colors.text }}
      >
        <Database size={13} />
        <span>Primary Survey</span>
        {primaryData && (
          <span className="cd-dataset-count">
            {primaryData.total_rows?.toLocaleString()} rows
          </span>
        )}
      </button>
      <button
        onClick={() => setActiveDataset('backcheck')}
        disabled={!backcheckData}
        className={`cd-dataset-tab ${activeDataset === 'backcheck' ? 'active' : ''}`}
        style={{ color: colors.text }}
      >
        <CheckCircle size={13} />
        <span>Back-Check</span>
        {backcheckData && (
          <span className="cd-dataset-count">
            {backcheckData.total_rows?.toLocaleString()} rows
          </span>
        )}
      </button>
    </div>
  );

  // Summary Cards - Matching DataSummary style
  const SummaryCards = () => {
    if (!collectorResults) return null;

    const { summary, total_collectors, total_interviews } = collectorResults;

    return (
      <>
      <div className="cd-stats-grid">
        {(() => {
          // Local MetricCard — mirrors the Uniqueness KEY METRICS card layout exactly:
          // icon chip on the left, centered text block (label / value / subtitle),
          // phantom spacer on the right, glass background, severity bracket + pulse beacon.
          const MetricCard = ({ title, value, subtitle, Icon, severity }) => {
            const iconColor = severity === 'critical'
              ? (isDarkMode ? '#f87171' : '#b91c1c')
              : severity === 'warning'
              ? (isDarkMode ? '#fbbf24' : '#b45309')
              : (isDarkMode ? '#60a5fa' : '#2563EB');
            const sevColor = severity ? iconColor : null;

            return (
              <div className="nsx-metric-card" style={{
                position: 'relative',
                padding: '18px',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                borderRadius: '8px',
                border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
                overflow: 'hidden',
                fontFamily: "'Outfit', -apple-system, sans-serif",
              }}>
                {severity && (
                  <svg
                    width="30" height="30" viewBox="0 0 30 30"
                    style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
                  >
                    <path
                      d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
                      stroke={sevColor} strokeWidth="2" fill="none"
                      strokeLinecap="round" opacity={0.9}
                    />
                  </svg>
                )}
                {severity && (
                  <div style={{
                    position: 'absolute', top: '10px', right: '10px',
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: sevColor,
                    boxShadow: `0 0 6px ${sevColor}88`,
                    animation: 'statusBreathe 2s ease-in-out infinite'
                  }} />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '5px',
                    background: severity
                      ? (isDarkMode
                          ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                          : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
                      : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, color: iconColor,
                  }}>
                    <Icon size={16} strokeWidth={1.75} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                    <div style={{
                      fontSize: '10px', fontWeight: 500,
                      letterSpacing: '0.16em', textTransform: 'lowercase',
                      color: isDarkMode ? '#6B7280' : '#9CA3AF',
                      marginBottom: '10px',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {title}
                    </div>
                    <div className="metric-value" style={{
                      fontSize: '32px', fontWeight: 500,
                      letterSpacing: '-0.02em',
                      fontVariantNumeric: 'tabular-nums',
                      color: isDarkMode ? '#E2E8F0' : '#1F2937',
                      lineHeight: 1.05, marginBottom: '6px',
                    }}>
                      {value}
                    </div>
                    {subtitle && (
                      <div style={{
                        fontSize: '11.5px', fontWeight: 400,
                        letterSpacing: '-0.005em',
                        color: isDarkMode ? '#6B7280' : '#9CA3AF',
                        lineHeight: 1.4,
                      }}>
                        {subtitle}
                      </div>
                    )}
                  </div>
                  <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
                </div>
              </div>
            );
          };

          // Severity computations
          const qScore = summary?.avg_quality_score || 0;
          const qSeverity = qScore < 70 ? 'critical' : qScore < 85 ? 'warning' : null;
          const flagCount = summary?.collectors_with_flags || 0;
          const flagSeverity = flagCount > 5 ? 'critical' : flagCount > 0 ? 'warning' : null;

          return (
            <>
              <MetricCard
                title="data collectors"
                value={<AnimatedNumber value={total_collectors} duration={800} />}
                subtitle="Monitoring performance"
                Icon={Users}
              />
              <MetricCard
                title="total interviews"
                value={<AnimatedNumber value={total_interviews} duration={800} />}
                subtitle="Across all collectors"
                Icon={Target}
              />
              <MetricCard
                title="avg quality score"
                value={<AnimatedNumber value={qScore} decimals={1} suffix="%" duration={800} />}
                subtitle={qSeverity === 'critical' ? 'Critical — review collectors' : qSeverity === 'warning' ? 'Below target — investigate' : 'Healthy performance'}
                Icon={Award}
                severity={qSeverity}
              />
              <MetricCard
                title="with flags"
                value={<AnimatedNumber value={flagCount} duration={800} />}
                subtitle={flagSeverity ? 'Collectors flagged for review' : 'No flags detected'}
                Icon={AlertTriangle}
                severity={flagSeverity}
              />
            </>
          );
        })()}
      </div>

      {/* XLSForm Integration Status Banner */}
      {collectorResults?._xlsform_integrated && (
        <div style={{
          backgroundColor: isDarkMode ? 'rgba(4, 120, 87, 0.08)' : 'rgba(4, 120, 87, 0.05)',
          border: `0.5px solid ${isDarkMode ? 'rgba(52, 211, 153, 0.25)' : 'rgba(4, 120, 87, 0.2)'}`,
          borderRadius: '2px',
          padding: '10px 14px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CheckCircle size={18} style={{ color: '#10B981' }} />
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#10B981' }}>
              XLSForm Integration Active
            </span>
            <span style={{ fontSize: '12px', color: colors.textSecondary }}>
              — Quality scores account for skip logic and constraint violations
            </span>
          </div>
          {collectorResults?.xlsform_summary && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {collectorResults.xlsform_summary.total_constraint_violations > 0 && (
                <span style={{
                  fontSize: '12px',
                  color: colors.warning,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <AlertTriangle size={14} />
                  {collectorResults.xlsform_summary.total_constraint_violations} constraint violations
                </span>
              )}
              {collectorResults.xlsform_summary.collectors_with_violations > 0 && (
                <span style={{
                  fontSize: '12px',
                  backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  color: colors.warning
                }}>
                  {collectorResults.xlsform_summary.collectors_with_violations} collectors affected
                </span>
              )}
            </div>
          )}
        </div>
      )}
      </>
    );
  };

  // Collector Table
  const CollectorTable = () => {
    const [expandedCollector, setExpandedCollector] = useState(null);
    const barsReady = useAnimateOnMount(500);
    if (!collectorResults) return null;

    // Local helpers for new design tokens
    const gradeClass = (g) => {
      const k = String(g || '').toUpperCase();
      if (k === 'A') return 'cd-grade--a';
      if (k === 'B') return 'cd-grade--b';
      if (k === 'C') return 'cd-grade--c';
      return 'cd-grade--d';
    };
    const barClass = (pct) => {
      if (pct >= 90) return 'cd-bar-fill--green';
      if (pct >= 70) return 'cd-bar-fill--amber';
      return 'cd-bar-fill--red';
    };

    return (
      <div className="cd-card">
        {/* Table Header Controls */}
        <div className="cd-card-header">
          <h3 className="cd-card-title" style={{ color: colors.text }}>
            <Users size={16} />
            Collector Performance
          </h3>
          <div className="cd-controls-right">
            <div className="cd-search">
              <Search size={13} style={{ opacity: 0.5 }} />
              <input
                type="text"
                placeholder="Search collector…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button
              onClick={() => setFilterFlags(!filterFlags)}
              className={`cd-btn ${filterFlags ? 'active' : ''}`}
            >
              <Filter size={13} />
              Flagged only
            </button>

            <button
              onClick={handleRefreshAnalysis}
              disabled={isLoading || !currentData}
              className="cd-btn"
              title="Re-analyze data (clears cached results)"
            >
              <RefreshCw size={13} className={isLoading ? 'spin' : ''} />
              Refresh
            </button>

            {currentLastAnalyzedAt && !isLoading && (
              <span className="cd-cache-indicator" style={{ color: colors.textMuted }}>
                <CheckCircle size={12} style={{ color: colors.success }} />
                Cached: {new Date(currentLastAnalyzedAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="cd-table-wrap">
          <table className="cd-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('collector_id')} style={{ color: colors.textMuted }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    collector id
                    <ArrowUpDown size={11} />
                  </div>
                </th>
                <th onClick={() => handleSort('total_interviews')} style={{ color: colors.textMuted }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    interviews
                    <ArrowUpDown size={11} />
                  </div>
                </th>
                <th onClick={() => handleSort('interviews_per_day')} style={{ color: colors.textMuted }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    per day
                    <ArrowUpDown size={11} />
                  </div>
                </th>
                <th onClick={() => handleSort('active_days')} style={{ color: colors.textMuted }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    active days
                    <ArrowUpDown size={11} />
                  </div>
                </th>
                <th onClick={() => handleSort('completeness_pct')} style={{ color: colors.textMuted }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    completeness
                    <ArrowUpDown size={11} />
                  </div>
                </th>
                <th onClick={() => handleSort('quality_score')} style={{ color: colors.textMuted }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    quality score
                    <ArrowUpDown size={11} />
                  </div>
                </th>
                <th style={{ color: colors.textMuted, cursor: 'default' }}>flags</th>
                <th style={{ color: colors.textMuted, cursor: 'default', width: '44px' }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedCollectors.map((collector) => (
                <React.Fragment key={collector.collector_id}>
                  <tr
                    onClick={() => setExpandedCollector(
                      expandedCollector === collector.collector_id ? null : collector.collector_id
                    )}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="cd-id-cell">
                        <div className="cd-id-avatar">
                          <User size={13} />
                        </div>
                        <span style={{ fontWeight: 500, letterSpacing: '-0.005em', color: colors.text }}>
                          {collector.collector_id}
                        </span>
                      </div>
                    </td>
                    <td style={{ color: colors.text }}>
                      <span style={{ fontWeight: 500, letterSpacing: '-0.005em' }}>{collector.total_interviews}</span>
                      <span style={{ color: colors.textMuted, fontSize: '11.5px', marginLeft: '5px' }}>
                        ({collector.interview_pct}%)
                      </span>
                    </td>
                    <td style={{ color: colors.text, letterSpacing: '-0.005em' }}>{collector.interviews_per_day}</td>
                    <td style={{ color: colors.text, letterSpacing: '-0.005em' }}>{collector.active_days || '—'}</td>
                    <td>
                      <div className="cd-bar">
                        <div className="cd-bar-track">
                          <div
                            className={`cd-bar-fill ${barClass(collector.completeness_pct)}`}
                            style={{ width: barsReady ? `${collector.completeness_pct}%` : '0%' }}
                          />
                        </div>
                        <span className="cd-bar-label" style={{ color: colors.text }}>
                          {barsReady ? <AnimatedNumber value={collector.completeness_pct} decimals={0} suffix="%" delay={0} duration={800} /> : '0%'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="cd-bar">
                        <div className="cd-bar-track">
                          <div
                            className={`cd-bar-fill ${barClass(collector.quality_score)}`}
                            style={{ width: barsReady ? `${collector.quality_score}%` : '0%' }}
                          />
                        </div>
                        <span className="cd-bar-label" style={{ color: getQualityColor(collector.quality_score, isDarkMode) }}>
                          {barsReady ? <AnimatedNumber value={collector.quality_score} decimals={0} suffix="%" delay={0} duration={800} /> : '0%'}
                        </span>
                        <span className={`cd-grade ${gradeClass(collector.quality_grade)}`}>
                          {collector.quality_grade}
                        </span>
                      </div>
                    </td>
                    <td>
                      {collector.flags && collector.flags.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {collector.flags.map((flag, i) => (
                            <span key={i} className="cd-flag cd-flag--warn">
                              {flag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="cd-flag cd-flag--none">
                          <CheckCircle size={11} />
                          clean
                        </span>
                      )}
                    </td>
                    <td>
                      <button className="cd-expand-btn" style={{ color: colors.textMuted }}>
                        {expandedCollector === collector.collector_id ?
                          <ChevronUp size={14} /> : <ChevronDown size={14} />
                        }
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedCollector === collector.collector_id && (
                    <tr>
                      <td colSpan={8} style={{
                        padding: '16px',
                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)',
                      }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                          gap: '12px'
                        }}>
                          {/* Duration Stats */}
                          {collector.duration_stats && (
                            <div style={{
                              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                              borderRadius: '2px',
                              padding: '14px',
                              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
                            }}>
                              <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '10px',
                                fontWeight: 500,
                                letterSpacing: '0.16em',
                                textTransform: 'lowercase',
                                color: colors.textMuted,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <Clock size={12} />
                                duration statistics
                              </h4>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                {[
                                  ['mean',   collector.duration_stats.mean],
                                  ['median', collector.duration_stats.median],
                                  ['min',    collector.duration_stats.min],
                                  ['max',    collector.duration_stats.max],
                                ].map(([k, v]) => (
                                  <div key={k}>
                                    <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted }}>
                                      {k}
                                    </div>
                                    <div style={{ fontWeight: 500, color: colors.text, fontSize: '13px', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.005em' }}>
                                      {v?.toFixed(1)} min
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {collector.duration_outliers > 0 && (
                                <div style={{
                                  marginTop: '10px',
                                  padding: '7px 10px',
                                  backgroundColor: isDarkMode ? 'rgba(251, 191, 36, 0.1)' : 'rgba(217, 119, 6, 0.08)',
                                  borderRadius: '2px',
                                  fontSize: '11.5px',
                                  color: isDarkMode ? '#fbbf24' : '#b45309',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  letterSpacing: '-0.005em',
                                }}>
                                  <AlertTriangle size={11} />
                                  {collector.duration_outliers} duration outliers detected
                                </div>
                              )}
                            </div>
                          )}

                          {/* Activity Period */}
                          <div style={{
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                            borderRadius: '2px',
                            padding: '14px',
                            border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
                          }}>
                            <h4 style={{
                              margin: '0 0 10px 0',
                              fontSize: '10px',
                              fontWeight: 500,
                              letterSpacing: '0.16em',
                              textTransform: 'lowercase',
                              color: colors.textMuted,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <Calendar size={12} />
                              activity period
                            </h4>
                            <div style={{ display: 'grid', gap: '8px' }}>
                              <div>
                                <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted }}>
                                  first interview
                                </div>
                                <div style={{ fontWeight: 500, color: colors.text, fontSize: '13px', letterSpacing: '-0.005em' }}>
                                  {collector.first_interview ?
                                    new Date(collector.first_interview).toLocaleDateString() : '—'}
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted }}>
                                  last interview
                                </div>
                                <div style={{ fontWeight: 500, color: colors.text, fontSize: '13px', letterSpacing: '-0.005em' }}>
                                  {collector.last_interview ?
                                    new Date(collector.last_interview).toLocaleDateString() : '—'}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Quality Dimension Scores */}
                          {collector.dimension_scores && (
                            <div style={{
                              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                              borderRadius: '2px',
                              padding: '14px',
                              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
                            }}>
                              <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '10px',
                                fontWeight: 500,
                                letterSpacing: '0.16em',
                                textTransform: 'lowercase',
                                color: colors.textMuted,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <Award size={12} />
                                quality dimensions
                              </h4>
                              {Object.entries(collector.dimension_scores).map(([dim, score]) => (
                                <div key={dim} style={{ marginBottom: '8px' }}>
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: '11.5px',
                                    marginBottom: '3px',
                                    letterSpacing: '-0.005em',
                                  }}>
                                    <span style={{ color: colors.textMuted, textTransform: 'capitalize' }}>
                                      {dim.replace(/_/g, ' ')}
                                    </span>
                                    <span style={{ color: getQualityColor(score, isDarkMode), fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                                      {score}%
                                    </span>
                                  </div>
                                  <div className="cd-bar-track" style={{ width: '100%' }}>
                                    <div
                                      className={`cd-bar-fill ${barClass(score)}`}
                                      style={{ width: `${score}%` }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Geographic Coverage */}
                          {collector.geo_coverage && (
                            <div style={{
                              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                              borderRadius: '2px',
                              padding: '14px',
                              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
                            }}>
                              <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '10px',
                                fontWeight: 500,
                                letterSpacing: '0.16em',
                                textTransform: 'lowercase',
                                color: colors.textMuted,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <MapPin size={12} />
                                geographic coverage
                              </h4>
                              <div>
                                <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted }}>
                                  unique locations
                                </div>
                                <div style={{ fontWeight: 500, color: colors.text, fontSize: '13px', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.005em' }}>
                                  {collector.geo_coverage.unique_locations}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* XLSForm Quality Stats */}
                          {collector.xlsform_stats && (
                            <div style={{
                              backgroundColor: isDarkMode ? 'rgba(52, 211, 153, 0.06)' : 'rgba(4, 120, 87, 0.04)',
                              borderRadius: '2px',
                              padding: '14px',
                              border: `0.5px solid ${isDarkMode ? 'rgba(52, 211, 153, 0.25)' : 'rgba(4, 120, 87, 0.2)'}`
                            }}>
                              <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '10px',
                                fontWeight: 500,
                                letterSpacing: '0.16em',
                                textTransform: 'lowercase',
                                color: isDarkMode ? '#34d399' : '#047857',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <CheckCircle size={12} />
                                xlsform quality analysis
                              </h4>
                              <div style={{ display: 'grid', gap: '8px' }}>
                                {collector.xlsform_stats.completeness_info && (
                                  <>
                                    {collector.xlsform_stats.completeness_info.xlsform_applied && (
                                      <div>
                                        <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted }}>
                                          true missing (after skip logic)
                                        </div>
                                        <div style={{ fontWeight: 500, color: colors.text, fontSize: '13px', letterSpacing: '-0.005em' }}>
                                          {collector.xlsform_stats.completeness_info.true_missing || 0} fields
                                        </div>
                                      </div>
                                    )}
                                    {collector.xlsform_stats.completeness_info.legitimately_skipped > 0 && (
                                      <div>
                                        <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted }}>
                                          legitimately skipped
                                        </div>
                                        <div style={{ fontWeight: 500, color: isDarkMode ? '#34d399' : '#047857', fontSize: '13px', letterSpacing: '-0.005em' }}>
                                          {collector.xlsform_stats.completeness_info.legitimately_skipped} fields
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                                {collector.xlsform_stats.constraint_violations &&
                                 collector.xlsform_stats.constraint_violations.violations > 0 && (
                                  <div style={{
                                    marginTop: '6px',
                                    padding: '8px 10px',
                                    backgroundColor: isDarkMode ? 'rgba(251, 191, 36, 0.1)' : 'rgba(217, 119, 6, 0.08)',
                                    borderRadius: '2px'
                                  }}>
                                    <div style={{
                                      fontSize: '11.5px',
                                      fontWeight: 500,
                                      color: isDarkMode ? '#fbbf24' : '#b45309',
                                      marginBottom: '5px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '5px',
                                      letterSpacing: '-0.005em',
                                    }}>
                                      <AlertTriangle size={11} />
                                      {collector.xlsform_stats.constraint_violations.violations} constraint violations
                                    </div>
                                    {collector.xlsform_stats.constraint_violations.details?.slice(0, 3).map((detail, i) => (
                                      <div key={i} style={{
                                        fontSize: '10.5px',
                                        color: colors.textSecondary,
                                        marginTop: '3px',
                                        letterSpacing: '-0.005em',
                                      }}>
                                        <span style={{ fontWeight: 500 }}>{detail.field}:</span>{' '}
                                        value {detail.value} violates "{detail.constraint}"
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <div style={{
          padding: '10px 18px',
          borderTop: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          fontSize: '11px',
          fontWeight: 500,
          letterSpacing: '0.06em',
          textTransform: 'lowercase',
          color: colors.textMuted
        }}>
          showing {sortedCollectors.length} of {collectorResults.total_collectors} collectors
        </div>
      </div>
    );
  };

  // Timeline Tab Content
  const TimelineContent = () => {
    if (!timelineResults) return null;

    const { date_range, daily_distribution, hourly_distribution, day_of_week_distribution, anomalies } = timelineResults;

    // Find max values for scaling
    const maxHourlyCount = Math.max(...hourly_distribution.data.map(h => h.count));
    const maxDowCount = Math.max(...day_of_week_distribution.data.map(d => d.count));
    const barsReady = useAnimateOnMount(500);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Timeline Header Controls */}
        

        {/* Date Range Summary */}
        <AnimatedCard delay={0} translateY={12}>
        <div className="cd-stats-grid">
          {(() => {
            // Local MetricCard matching the Uniqueness KEY METRICS layout:
            // 36×36 icon chip + centered text (label / value / subtitle) + phantom spacer.
            // valueSize: 32 for numbers, smaller for text (date ranges, hour lists, etc.)
            const MetricCard = ({ title, value, subtitle, Icon, valueSize = 32 }) => {
              const iconColor = isDarkMode ? '#60a5fa' : '#2563EB';
              return (
                <div className="nsx-metric-card" style={{
                  position: 'relative',
                  padding: '18px',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                  borderRadius: '8px',
                  border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
                  overflow: 'hidden',
                  fontFamily: "'Outfit', -apple-system, sans-serif",
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '5px',
                      background: 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, color: iconColor,
                    }}>
                      <Icon size={16} strokeWidth={1.75} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                      <div style={{
                        fontSize: '10px', fontWeight: 500,
                        letterSpacing: '0.16em', textTransform: 'lowercase',
                        color: isDarkMode ? '#6B7280' : '#9CA3AF',
                        marginBottom: '10px',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {title}
                      </div>
                      <div className="metric-value" style={{
                        fontSize: `${valueSize}px`,
                        fontWeight: 500,
                        letterSpacing: '-0.02em',
                        fontVariantNumeric: 'tabular-nums',
                        color: isDarkMode ? '#E2E8F0' : '#1F2937',
                        lineHeight: 1.05, marginBottom: '6px',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {value}
                      </div>
                      {subtitle && (
                        <div style={{
                          fontSize: '11.5px', fontWeight: 400,
                          letterSpacing: '-0.005em',
                          color: isDarkMode ? '#6B7280' : '#9CA3AF',
                          lineHeight: 1.4,
                        }}>
                          {subtitle}
                        </div>
                      )}
                    </div>
                    <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
                  </div>
                </div>
              );
            };

            return (
              <>
                <MetricCard
                  title="collection period"
                  value={`${new Date(date_range.start).toLocaleDateString()} – ${new Date(date_range.end).toLocaleDateString()}`}
                  subtitle="Start — End"
                  Icon={Calendar}
                  valueSize={16}
                />
                <MetricCard
                  title="active days"
                  value={<AnimatedNumber value={date_range.active_days} duration={800} />}
                  subtitle={`of ${date_range.total_days} total days`}
                  Icon={Activity}
                />
                <MetricCard
                  title="avg interviews/day"
                  value={<AnimatedNumber value={daily_distribution.stats.mean} decimals={1} duration={800} />}
                  subtitle="Mean across active days"
                  Icon={TrendingUp}
                />
                <MetricCard
                  title="peak hours"
                  value={hourly_distribution.peak_hours.map(h => h.label).join(', ')}
                  subtitle="Highest-volume windows"
                  Icon={Clock}
                  valueSize={18}
                />
              </>
            );
          })()}
        </div>
        </AnimatedCard>

        {/* Hourly Distribution Chart */}
        <AnimatedCard delay={80} translateY={12}>
        <div className="cd-card with-nodes">
          <span className="cn-bl" /><span className="cn-br" />
          <div className="cd-card-header">
            <h3 className="cd-card-title" style={{ color: colors.text }}>
              <Clock size={16} />
              Hourly Distribution
            </h3>
          </div>
          <div className="cd-card-body">
            <div className="cd-chart cd-chart--hourly">
              {hourly_distribution.data.map((hour) => {
                const heightPct = maxHourlyCount > 0 ? (hour.count / maxHourlyCount) * 100 : 0;
                const isPeak = hourly_distribution.peak_hours.some(p => p.hour === hour.hour);
                return (
                  <div
                    key={hour.hour}
                    className={`cd-chart-col ${isPeak ? 'peak' : ''}`}
                    title={`${hour.label}: ${hour.count} interviews`}
                  >
                    <div
                      className="cd-chart-bar"
                      style={{
                        height: barsReady ? `${heightPct}%` : '0%',
                      }}
                    />
                    {hour.hour % 3 === 0 && (
                      <span className="cd-chart-tick" style={{ color: colors.textMuted }}>
                        {hour.label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </AnimatedCard>

        {/* Day of Week Distribution */}
        <AnimatedCard delay={160} translateY={12}>
        <div className="cd-card">
          <div className="cd-card-header">
            <h3 className="cd-card-title" style={{ color: colors.text }}>
              <Calendar size={16} />
              Day of Week Distribution
            </h3>
          </div>
          <div className="cd-card-body">
            <div className="cd-chart cd-chart--dow">
              {day_of_week_distribution.data.map((day) => {
                const heightPct = maxDowCount > 0 ? (day.count / maxDowCount) * 100 : 0;
                const isWeekend = day.day >= 5;
                return (
                  <div
                    key={day.day}
                    className={`cd-dow-col ${isWeekend ? 'weekend' : ''}`}
                  >
                    <div className="cd-dow-track">
                      <div
                        className="cd-dow-bar"
                        style={{
                          height: barsReady ? `${heightPct}%` : '0%',
                        }}
                      />
                    </div>
                    <div className="cd-dow-label" style={{ color: isWeekend ? undefined : colors.text }}>
                      {day.short_name}
                    </div>
                    <div className="cd-dow-count" style={{ color: colors.textMuted }}>
                      {day.count.toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="cd-chart-footnote" style={{ color: colors.textMuted }}>
              Weekend interviews: {day_of_week_distribution.weekend_pct}% of total
            </div>
          </div>
        </div>
        </AnimatedCard>

        {/* Anomalies */}
        {anomalies && anomalies.length > 0 && (
          <AnimatedCard delay={240} translateY={12}>
          <div className="cd-card">
            <div className="cd-card-header">
              <h3 className="cd-card-title" style={{ color: colors.text }}>
                <AlertTriangle size={16} style={{ color: colors.warning }} />
                Timeline Anomalies
              </h3>
            </div>
            <div className="cd-card-body">
              {anomalies.map((anomaly, idx) => (
                <div
                  key={idx}
                  className={`cd-anomaly ${anomaly.severity === 'high' ? 'cd-anomaly--high' : 'cd-anomaly--med'}`}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <span style={{
                      padding: '1px 7px',
                      borderRadius: '100px',
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'lowercase',
                      backgroundColor: anomaly.severity === 'high'
                        ? (isDarkMode ? 'rgba(248, 113, 113, 0.18)' : 'rgba(220, 38, 38, 0.14)')
                        : (isDarkMode ? 'rgba(251, 191, 36, 0.18)' : 'rgba(217, 119, 6, 0.14)'),
                      color: anomaly.severity === 'high'
                        ? (isDarkMode ? '#f87171' : '#dc2626')
                        : (isDarkMode ? '#fbbf24' : '#b45309'),
                    }}>
                      {anomaly.severity}
                    </span>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: colors.text,
                      textTransform: 'capitalize',
                      letterSpacing: '-0.005em',
                    }}>
                      {anomaly.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div style={{ fontSize: '12.5px', color: colors.textMuted, letterSpacing: '-0.005em' }}>
                    {anomaly.description ||
                      (anomaly.date && `Date: ${anomaly.date}, Count: ${anomaly.count}`)
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
          </AnimatedCard>
        )}
      </div>
    );
  };

  return (
    <div className={`cd-container ${isDarkMode ? 'dark' : 'light'}`} style={{
      backgroundColor: 'transparent',
      padding: isMobile ? '20px 14px 32px' : undefined,
    }}>
      {/* Loading Overlay */}
      <AILoadingOverlay
        isVisible={isLoading}
        context="quality_analysis"
        title={activeTab === 'collectors' ? 'Analyzing Collectors' : 'Analyzing Timeline'}
        subtitle="Processing data patterns and metrics"
        realTimeStats={{
          totalRows: currentData?.total_rows || currentData?.full_data?.length || currentData?.data?.length || 0,
          columnsAnalyzed: currentData?.column_types 
            ? Object.keys(currentData.column_types).length 
            : (currentData?.total_variables || currentData?.columns?.length || 0),
        }}
        showDetailedLogs={true}
      />

      {/* Header */}
      <div className="cd-header">
        <div>
          <h1 className="cd-title" style={{ color: colors.text }}>
            <Users size={22} />
            Collector &amp; Timeline Analysis
          </h1>
          <p className="cd-subtitle" style={{ color: colors.textSecondary }}>
            Monitor data collector performance and collection patterns
          </p>
        </div>
      </div>

      {/* Dataset Pills + Sub-tabs on same row */}
      <div className="cd-tabs-row">
        <DatasetToggle />
        <div className="cd-subtabs">
          <button
            onClick={() => setActiveTab('collectors')}
            className={`cd-subtab ${activeTab === 'collectors' ? 'active' : ''}`}
            style={{ color: colors.text }}
          >
            <Users size={14} />
            Collector Performance
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`cd-subtab ${activeTab === 'timeline' ? 'active' : ''}`}
            style={{ color: colors.text }}
          >
            <Calendar size={14} />
            Timeline Patterns
          </button>
        </div>
      </div>

      {/* Controls Row - Collector Performance Tab Only */}
      {activeTab === 'collectors' && (
      <div className="cd-controls">
        <div className="cd-controls-left">
          <div className="cd-select-group">
            <Users size={14} className="cd-select-icon" style={{ color: colors.textMuted }} />
            <select
              className="cd-select"
              value={internalCollectorColumn || externalCollectorIdColumn || ''}
              onChange={(e) => {
                setInternalCollectorColumn(e.target.value);
                setCollectorResults(null);
              }}
            >
              <option value="">Collector column...</option>
              {potentialCollectorColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="cd-controls-right">
          <button
            onClick={handleRefreshAnalysis}
            disabled={isLoading || !collectorIdColumn}
            className="cd-btn"
            title="Re-analyze data (clears cached results)"
          >
            <RefreshCw size={16} className={isLoading ? 'spin' : ''} />
            Refresh
          </button>

          {/* Last analyzed indicator */}
          {lastAnalyzedAt.collectors && !isLoading && (
            <span className="cd-cache-indicator" style={{ color: colors.textMuted }}>
              <CheckCircle size={12} style={{ color: colors.success }} />
              Cached: {new Date(lastAnalyzedAt.collectors).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          )}
        </div>
      </div>
      )}

      {/* Controls Row - Timeline Patterns Tab Only */}
      {activeTab === 'timeline' && (
      <div className="cd-controls">
        <div className="cd-controls-left">
          <div className="cd-select-group">
            <Calendar size={14} className="cd-select-icon" style={{ color: colors.textMuted }} />
            <select
              value={timelineDateColumn}
              onChange={(e) => {
                setTimelineDateColumn(e.target.value);
                setTimelineResults(null);
              }}
              className={`cd-select ${!timelineDateColumn ? 'required-missing' : ''}`}
            >
              <option value="">Select date column…</option>
              {potentialDateColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
          </div>

          
          {/* Duration Column Selector (Optional for Timeline) */}
          <div className="cd-select-group">
            <Clock size={14} className="cd-select-icon" style={{ color: colors.textMuted }} />
            <select
              value={timelineDurationColumn}
              onChange={(e) => {
                setTimelineDurationColumn(e.target.value);
                setTimelineResults(null);
              }}
              className="cd-select"
            >
              <option value="">Duration (optional)</option>
              {potentialDurationColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="cd-controls-right">
          <button
            onClick={handleRefreshAnalysis}
            disabled={isLoading || !timelineDateColumn}
            className="cd-btn"
            title="Re-analyze timeline (clears cached results)"
          >
            <RefreshCw size={14} className={isLoading ? 'spin' : ''} />
            Refresh
          </button>

          {/* Last analyzed indicator */}
          {lastAnalyzedAt.timeline && !isLoading && (
            <span className="cd-cache-indicator" style={{ color: colors.textMuted }}>
              <CheckCircle size={12} style={{ color: colors.success }} />
              Cached: {new Date(lastAnalyzedAt.timeline).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          )}
        </div>
      </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: `${colors.error}10`,
          border: `1px solid ${colors.error}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: colors.error
        }}>
          <AlertCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {error}
        </div>
      )}

      {/* Content */}
      {activeTab === 'collectors' && (
        <>
          <AnimatedCard delay={0} translateY={12} key="summary-cards">
          <SummaryCards />
          </AnimatedCard>
          <AnimatedCard delay={80} translateY={12} key="collector-table">
          <CollectorTable />
          </AnimatedCard>
        </>
      )}

      {activeTab === 'timeline' && (
        timelineResults ? (
          <TimelineContent />
        ) : !timelineDateColumn ? (
          <div className="nsx-metric-card" style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.cardBorder}`
          }}>
            <Calendar size={48} style={{ color: colors.textMuted, marginBottom: '16px' }} />
            <h3 style={{ color: colors.text, marginBottom: '12px' }}>Timeline Analysis</h3>
            <p style={{ color: colors.textMuted }}>
              Please select a date column from the dropdown above to enable timeline analysis.
            </p>
          </div>
        ) : (
          <div className="nsx-metric-card" style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.cardBorder}`
          }}>
            <RefreshCw size={48} style={{ color: colors.textMuted, marginBottom: '16px' }} />
            <h3 style={{ color: colors.text, marginBottom: '12px' }}>Ready to Analyze</h3>
            <p style={{ color: colors.textMuted, marginBottom: '16px' }}>
              Click the Refresh button above to start timeline analysis.
            </p>
            <button
              onClick={() => fetchTimelineAnalysis(true)}
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: colors.primary,
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Activity size={16} />
              Analyze Timeline
            </button>
          </div>
        )
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default CollectorDashboard;